import type { TicketEvenement } from '../../Composants/ticket-evenements/ticket-evenements';
import { CONFIG_ETATS_TICKET, TicketState } from '../../Services/ticket-state';

/**
 * Conserve l’événement d’état lorsqu’une même action produit aussi une fermeture.
 * L’appariement exige le même instant et le même acteur ; les fermetures isolées restent visibles.
 * Renvoie une nouvelle liste dans l’ordre initial, sans modifier les événements reçus.
 */
export function masquerFermeturesRedondantes(
  evenements: readonly TicketEvenement[],
): TicketEvenement[] {
  // Compter d’abord les résolutions rend l’appariement indépendant de l’ordre des deux événements.
  const resolutions = new Map<string, number>();
  for (const evenement of evenements) {
    const valeur = evenement.valeur_apres?.trim().toLowerCase();
    if (
      evenement.champ !== 'etat' ||
      (valeur !== CONFIG_ETATS_TICKET.resolu.etat && valeur !== TicketState.resolu)
    )
      continue;
    const cle = cleAction(evenement);
    if (cle !== null) resolutions.set(cle, (resolutions.get(cle) ?? 0) + 1);
  }
  return evenements.filter((evenement) => {
    if (evenement.champ !== 'ferme' || evenement.valeur_apres !== '1') return true;
    const cle = cleAction(evenement);
    const nombre = cle === null ? 0 : (resolutions.get(cle) ?? 0);
    if (cle === null || nombre === 0) return true;
    // Apparier une seule fermeture par résolution, sans supprimer d’autres actions.
    resolutions.set(cle, nombre - 1);
    return false;
  });
}

/**
 * Identifie une action par son instant normalisé et l’identifiant de son acteur, sinon son nom.
 * Sans date valide ou acteur identifiable, renvoie null pour ne masquer aucun événement par erreur.
 */
function cleAction(evenement: TicketEvenement): string | null {
  const date = new Date(evenement.date).getTime();
  const personne = evenement.personne;
  if (!Number.isFinite(date) || !personne) return null;
  if (typeof personne === 'object' && personne.id != null) {
    return JSON.stringify([date, 'id', personne.id]);
  }
  const nom = (
    typeof personne === 'string' ? personne : `${personne.prenom ?? ''} ${personne.nom ?? ''}`
  )
    .trim()
    .replace(/\s+/g, ' ');
  return nom ? JSON.stringify([date, 'nom', nom]) : null;
}
